import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/Util';
import {SequenceType} from 'constants/SequenceConstants';
import {
  BasicPolymerInfo,
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  FragmentProductionMethod,
  MethodData,
  NormalizedFragment,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {BulkAssemblyValidationMessage} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {isBANumber} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {Cut, DigestableEnzyme} from 'molbio/enzymes/EnzymeConstants';
import {getCuts, getEnzyme} from 'molbio/enzymes/EnzymeUtil';
import {baseInExclusiveRange} from 'molbio/SeqUtil';

export class AssemblyFrontendFragmentValidator {
  constructor(
    readonly method: MethodData,
    readonly fragment: NormalizedFragment,
    readonly fragmentBin: BulkAssemblyFragmentBin | null
  ) {}

  private getRestrictionEnzymesForFragment():
    | {
        restrictionEnzyme5: DigestableEnzyme;
        restrictionEnzyme3: DigestableEnzyme;
      }
    | {
        restrictionEnzyme5: null;
        restrictionEnzyme3: null;
      } {
    if (
      this.method.type === BulkAssemblyMethod.GOLDEN_GATE &&
      this.getFragmentProductionMethod() === FragmentProductionMethod.EXISTING_CUT_SITES
    ) {
      return {
        restrictionEnzyme5: getEnzyme(this.method.parameters.defaultRestrictionEnzymeId),
        restrictionEnzyme3: getEnzyme(this.method.parameters.defaultRestrictionEnzymeId),
      };
    }
    return {restrictionEnzyme5: null, restrictionEnzyme3: null};
  }

  private getFragmentProductionMethod(): FragmentProductionMethod {
    return this.fragmentBin
      ? this.fragmentBin.fragmentProductionMethod
      : this.method.parameters.defaultFragmentProductionMethod;
  }

  private getOtherParticipatingRestrictionEnzymes(): Array<DigestableEnzyme> {
    if (
      this.method.type === BulkAssemblyMethod.GOLDEN_GATE &&
      this.getFragmentProductionMethod() === FragmentProductionMethod.PRIMER_PAIR
    ) {
      return [getEnzyme(this.method.parameters.defaultRestrictionEnzymeId)];
    }
    return [];
  }

  private getEntireCutsForRestrictionEnzyme(
    restrictionEnzyme: DigestableEnzyme,
    sequenceData: BasicPolymerInfo
  ): Array<Cut> {
    return getCuts(
      restrictionEnzyme,
      sequenceData.residues,
      // If restriction enzymes are specified, we can assume double-stranded DNA
      NucleotideType.DNA,
      SequenceType.DOUBLE_STRANDED,
      sequenceData.circular
    );
  }

  private filterToInternalCutsForRestrictionEnzyme(cutsites: Array<Cut>): Array<Cut> | null {
    let internalCuts: Array<Cut> | null = null;
    if (isBANumber(this.fragment.start) && isBANumber(this.fragment.end)) {
      const start = this.fragment.start.value;
      const end = this.fragment.end.value;
      internalCuts = cutsites.filter(({position}) => baseInExclusiveRange(position, start, end));
    }
    return internalCuts;
  }

  private getSingleRestrictionEnzymeValidationErrors(
    restrictionEnzyme: DigestableEnzyme,
    otherRestrictionEnzyme: DigestableEnzyme | null,
    sequenceData: BasicPolymerInfo
  ): Array<BulkAssemblyValidationMessage> {
    const messages: Array<BulkAssemblyValidationMessage> = [];
    const entireSequenceCuts = this.getEntireCutsForRestrictionEnzyme(restrictionEnzyme, sequenceData);

    const numCuts = entireSequenceCuts.length;
    const internalCuts = this.filterToInternalCutsForRestrictionEnzyme(entireSequenceCuts);
    const numInternalCuts = internalCuts?.length;
    if (internalCuts != null && numInternalCuts != null) {
      // First, check if there's any cut sites internal to the selection...
      if (numInternalCuts > 0) {
        const error = `This sequence has ${numCuts} ${restrictionEnzyme.name} ${pluralize(
          numCuts,
          'cut site',
          'cut sites'
        )}, ${numInternalCuts} of which ${pluralize(
          numInternalCuts,
          'occurs',
          'occur'
        )} in the middle of your fragment selection region.`;
        messages.push({status: ValidationStatus.WARNING, message: error});
      }
    } else if (
      // If both enzymes are the same, there must be two cut sites, not one
      (numCuts !== 2 &&
        otherRestrictionEnzyme != null &&
        restrictionEnzyme.id === otherRestrictionEnzyme.id) ||
      // If the enzymes are different, there can only be one cut site
      (numCuts !== 1 && otherRestrictionEnzyme != null && restrictionEnzyme.id !== otherRestrictionEnzyme.id)
    ) {
      // ...then, check that there is exactly one cut site in the entire sequence.
      const error = `This sequence has ${numCuts} ${restrictionEnzyme.name} ${pluralize(
        numCuts,
        'cut site',
        'cut sites'
      )}`;
      messages.push({
        status: ValidationStatus.WARNING,
        message: error,
      });
    }
    return messages;
  }

  /**
   * Get validation errors for a fragment that requires digestion using a restriction enzyme.
   *
   * This includes:
   * - there are cut sites internal to the fragment selection
   * - there are additional cut sites in the fragment (off-target sites)
   */
  private getRestrictionEnzymeValidationErrors(
    restrictionEnzyme5: DigestableEnzyme | null,
    restrictionEnzyme3: DigestableEnzyme | null,
    otherParticipatingRestrictionEnzymes: Array<DigestableEnzyme>,
    sequenceData: BasicPolymerInfo
  ): Array<BulkAssemblyValidationMessage> {
    const messages: Array<BulkAssemblyValidationMessage> = [];
    if (restrictionEnzyme5 != null) {
      messages.push(
        ...this.getSingleRestrictionEnzymeValidationErrors(
          restrictionEnzyme5,
          restrictionEnzyme3,
          sequenceData
        )
      );
    }
    if (
      restrictionEnzyme3 != null &&
      // Only perform validation if the second enzyme is different from the first
      (restrictionEnzyme5 == null || restrictionEnzyme5.id !== restrictionEnzyme3.id)
    ) {
      messages.push(
        ...this.getSingleRestrictionEnzymeValidationErrors(
          restrictionEnzyme3,
          restrictionEnzyme5,
          sequenceData
        )
      );
    }
    otherParticipatingRestrictionEnzymes.forEach((restrictionEnzyme) => {
      const internalCuts = this.filterToInternalCutsForRestrictionEnzyme(
        this.getEntireCutsForRestrictionEnzyme(restrictionEnzyme, sequenceData)
      );
      const numInternalCuts = internalCuts != null ? internalCuts.length : null;
      if (internalCuts != null && numInternalCuts != null && numInternalCuts > 0) {
        const error = `There ${pluralize(numInternalCuts, 'is', 'are')} ${numInternalCuts} ${
          restrictionEnzyme.name
        } ${pluralize(numInternalCuts, 'cut site', 'cut sites')} in the middle of your selected fragment`;
        messages.push({
          status: ValidationStatus.WARNING,
          message: error,
        });
      }
    });
    return messages;
  }

  getAssemblyFrontendFragmentValidationErrors(
    sequenceData: BasicPolymerInfo | null
  ): Array<BulkAssemblyValidationMessage> {
    const messages: Array<BulkAssemblyValidationMessage> = [];
    const {restrictionEnzyme5, restrictionEnzyme3} = this.getRestrictionEnzymesForFragment();

    if (sequenceData != null) {
      messages.push(
        ...this.getRestrictionEnzymeValidationErrors(
          restrictionEnzyme5,
          restrictionEnzyme3,
          this.getOtherParticipatingRestrictionEnzymes(),
          sequenceData
        )
      );
    }
    return messages;
  }
}
